/* istanbul ignore file */

import React, {
  type ComponentProps,
  memo,
  useCallback,
  useEffect,
} from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { type ColorValue, ScrollView, StyleSheet, View } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedRef,
  useAnimatedStyle,
  useScrollViewOffset,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { LI, UL } from '@expo/html-elements';
import { LinearGradient } from 'expo-linear-gradient';
import { type InterpreterFrom } from 'xstate';
import {
  IllusEmpty_1,
  LoadingPlaceholder,
  theme,
  useResponsive,
} from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import {
  type createLocationSearchMachine,
  type LocationGroups,
} from '../../machine';
import { type LocationResultsCard } from '../LocationResults/subcomponents';
import { LocationResultsListInfoMessage } from '../LocationResultsListInfoMessage';
import {
  LocationGroupCard,
  LocationPrediction,
  LocationsGroupTitle,
} from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchPredictionsList = memo((props: Props) => {
  const {
    predictions,
    predictionLocations,
    locationSearchService,
    interactedLocationRestaurantSlug,
    isLoading,
    hasNoPredictions,
    onPress,
    onCardPress,
    onCardFocus,
    onCardLayout,
    onCardUnmount,
  } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  // ─── Refs ────────────────────────────────────────────────────

  const scrollContainerAnimatedRef = useAnimatedRef<Animated.ScrollView>();

  // ─── Telemetry ───────────────────────────────────────────────

  const onLocationPredictionPress = useCallback(
    (id: string, predictionAddress: string) => {
      track('location.search_prediction.press', { predictionAddress });
      onPress(id);
    },
    [onPress, track],
  );

  const onLocationCardPress = useCallback<OnCardPress>(
    (location) => {
      track('location.search_prediction.location-press', {
        restaurantName: location?.restaurantName,
      });

      onCardPress(location);
    },
    [onCardPress, track],
  );

  // ─── Animations ──────────────────────────────────────────────

  const scrollOffset = useScrollViewOffset(scrollContainerAnimatedRef);
  const loadingSV = useSharedValue(0);

  const toggleLoadingSV = useCallback(() => {
    loadingSV.value = withTiming(isLoading ? 1 : 0, { duration: theme.transitions.base }); // prettier-ignore
  }, [isLoading, loadingSV]);

  // ─── Styles ──────────────────────────────────────────────────

  const scrollShadowAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: interpolate(scrollOffset.value, [0, 20], [0, 1]) };
  });

  const contentAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: interpolate(loadingSV.value, [1, 0], [0, 1]) };
  });

  const loadingContainerAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: interpolate(loadingSV.value, [1, 0], [1, 0]) };
  });

  const outerContainerStyles = match([styles.outerContainerXS, styles.outerContainerSM]); // prettier-ignore
  const containerStyles = match([styles.containerXS, styles.containerSM]);
  const contentContainerStyles = match([styles.contentContainerXS, styles.contentContainerSM]); // prettier-ignore

  const loadingContainerStyles = [
    styles.loadingContainer,
    match([styles.loadingContainerXS, styles.loadingContainerSM]),
    loadingContainerAnimatedStyles,
  ];

  const innerContainerStyles = [
    styles.innerContentContainer,
    contentAnimatedStyles,
  ];

  // ─── Derived Data ────────────────────────────────────────────────────

  const allPredictionLocations = [
    ...(predictionLocations?.pickup ?? []),
    ...(predictionLocations?.outpost ?? []),
  ];

  // ─── Effects ─────────────────────────────────────────────────

  useEffect(toggleLoadingSV, [toggleLoadingSV]);

  // ─────────────────────────────────────────────────────────────────────

  if (hasNoPredictions) {
    return (
      <ScrollView
        style={containerStyles}
        contentContainerStyle={contentContainerStyles}
      >
        <LocationResultsListInfoMessage
          title={formatMessage(messages.noResultsTitle)}
          text={formatMessage(messages.noResultsText)}
          illustration={IllusEmpty_1}
          illustrationAriaLabel="No results"
        />
      </ScrollView>
    );
  }

  return (
    <View style={outerContainerStyles}>
      <Animated.View
        style={[styles.shadowContainer, scrollShadowAnimatedStyles]}
        pointerEvents="none"
      >
        <LinearGradient
          locations={SHADOW_GRADIENT_LOCATIONS}
          colors={SHADOW_GRADIENT_COLORS}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>

      <Animated.ScrollView
        ref={scrollContainerAnimatedRef}
        style={containerStyles}
        contentContainerStyle={contentContainerStyles}
      >
        <Animated.View style={loadingContainerStyles} pointerEvents="none">
          <LoadingPlaceholder rows={5} rowHeight={44} palette="cream" />
        </Animated.View>

        <Animated.View style={innerContainerStyles}>
          {predictions.length > 0 ? (
            <UL>
              <LocationsGroupTitle>
                <FormattedMessage {...messages.locations} />
              </LocationsGroupTitle>

              {predictions.map((prediction) => (
                <LI key={prediction.id}>
                  <LocationPrediction
                    prediction={prediction}
                    onPress={onLocationPredictionPress}
                  />
                </LI>
              ))}
            </UL>
          ) : null}

          {allPredictionLocations.length > 0 ? (
            <UL>
              <LocationsGroupTitle>
                <FormattedMessage {...messages.sgRestaurants} />
              </LocationsGroupTitle>

              {allPredictionLocations.map((location) => (
                <LI key={location.id} style={styles.cardsListItem}>
                  <LocationGroupCard
                    locationSearchService={locationSearchService}
                    location={location}
                    withBottomBorder={false}
                    withDetailsModal
                    isLoading={isLoading}
                    onPress={onLocationCardPress}
                    onLayout={onCardLayout}
                    onFocus={onCardFocus}
                    onUmount={onCardUnmount}
                    interactedLocationRestaurantSlug={interactedLocationRestaurantSlug} // prettier-ignore
                  />
                </LI>
              ))}
            </UL>
          ) : null}
        </Animated.View>
      </Animated.ScrollView>
    </View>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locations: {
    defaultMessage: 'Locations',
    description: 'Location Search | Predictions | Locations title',
  },
  sgRestaurants: {
    defaultMessage: 'SG Restaurants',
    description: 'Location Search | Predictions | SG Restaurants title',
  },
  noResultsTitle: {
    defaultMessage: "Ah snap peas! We couldn't find any locations.",
    description: 'Location Search | Predictions | No results title',
  },
  noResultsText: {
    defaultMessage: 'Try another search',
    description: 'Location Search | Predictions | No results text',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const SHADOW_GRADIENT_LOCATIONS = [0, 1] as const;
const SHADOW_GRADIENT_COLORS: [ColorValue, ColorValue] = [
  theme.colors.CREAM,
  theme.colors.OPACITY.CREAM.TRANSPARENT,
] as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainerXS: {
    ...StyleSheet.absoluteFillObject,
  },
  outerContainerSM: {
    flex: 1,
  },
  shadowContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 2,
    height: 80,
  },
  containerXS: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    paddingHorizontal: theme.spacing['2'],
  },
  containerSM: {
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['2'],
  },
  contentContainerXS: {
    paddingVertical: theme.spacing['7'],
  },
  contentContainerSM: {
    paddingTop: theme.spacing['3'],
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    minHeight: 300,
    paddingHorizontal: theme.spacing['3'],
  },
  loadingContainerXS: {
    paddingTop: theme.spacing['7'],
  },
  loadingContainerSM: {
    paddingTop: theme.spacing['3'],
  },
  innerContentContainer: {
    gap: theme.spacing['5'],
  },
  cardsListItem: {
    paddingHorizontal: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  locationSearchService: InterpreterFrom<ReturnType<typeof createLocationSearchMachine>>; // prettier-ignore
  predictions: Address[];
  predictionLocations: LocationGroups | undefined;
  interactedLocationRestaurantSlug: string | undefined;
  isLoading: boolean;
  hasNoPredictions: boolean;
  onCardPress: OnCardPress;
  onCardFocus?: LocationResultCardProps['onFocus'];
  onCardLayout?: LocationResultCardProps['onLayout'];
  onCardUnmount?: LocationResultCardProps['onUmount'];
  onPress: (addressId: string) => void;
};

type OnCardPress = (location: {
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;
}) => void;

type LocationResultCardProps = ComponentProps<typeof LocationResultsCard>;

type Address = {
  id: string;
  name?: string;
  address: string;
  // @ts-expect-error TS(2503): Cannot find namespace 'google'.
  matchedSubstrings?: readonly google.maps.places.PredictionSubstring[];
};
